import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Predefined tag colors
const TAG_COLORS = [
  '#0891b2', // cyan
  '#059669', // green
  '#d97706', // amber
  '#dc2626', // red
  '#7c3aed', // violet
  '#db2777', // pink
  '#2563eb', // blue
  '#65a30d', // lime
  '#ea580c', // orange
  '#6366f1', // indigo
];

const TagManager = ({ onTagChange, onClose }) => {
  const { t } = useLanguage();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTag, setEditingTag] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tags`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTags(data);
      }
    } catch (error) {
      console.error('Failed to load tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      toast.error(t('tag_name') || 'Enter tag name');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newTagName,
          color: newTagColor
        })
      });

      if (response.ok) {
        toast.success(t('tag_created') || 'Tag created!');
        setNewTagName('');
        setNewTagColor(TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)]);
        loadTags();
        onTagChange?.();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create tag');
      }
    } catch (error) {
      console.error('Failed to create tag:', error);
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateTag = async (tagId, name, color) => {
    try {
      const response = await fetch(`${API_URL}/api/tags/${tagId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, color })
      });

      if (response.ok) {
        toast.success(t('save') + '!');
        setEditingTag(null);
        loadTags();
        onTagChange?.();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update tag');
      }
    } catch (error) {
      console.error('Failed to update tag:', error);
      toast.error(error.message);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm(t('delete_tag_confirm') || 'Delete this tag? It will be removed from all bookmarks.')) return;

    try {
      const response = await fetch(`${API_URL}/api/tags/${tagId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(t('tag_deleted') || 'Tag deleted!');
        loadTags();
        onTagChange?.();
      } else {
        throw new Error('Failed to delete tag');
      }
    } catch (error) {
      console.error('Failed to delete tag:', error);
      toast.error(t('error_occurred'));
    }
  };

  const TagItem = ({ tag }) => {
    const isEditing = editingTag === tag.tag_id;
    const [editName, setEditName] = useState(tag.name);
    const [editColor, setEditColor] = useState(tag.color);

    return (
      <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent/10 transition-colors">
        {isEditing ? (
          <>
            <div className="relative">
              <input
                type="color"
                value={editColor}
                onChange={(e) => setEditColor(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer w-8 h-8"
              />
              <div
                className="w-8 h-8 rounded-full border-2 border-border cursor-pointer"
                style={{ backgroundColor: editColor }}
              />
            </div>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleUpdateTag(tag.tag_id, editName, editColor);
                if (e.key === 'Escape') setEditingTag(null);
              }}
              className="flex-1 px-2 py-1 text-sm border border-input rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring"
              autoFocus
            />
            <button
              onClick={() => handleUpdateTag(tag.tag_id, editName, editColor)}
              className="p-2 hover:bg-primary/10 rounded transition-colors text-primary"
              title={t('save')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button
              onClick={() => setEditingTag(null)}
              className="p-2 hover:bg-accent/20 rounded transition-colors"
              title={t('cancel')}
            >
              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </>
        ) : (
          <>
            <div
              className="w-4 h-4 rounded-full flex-shrink-0"
              style={{ backgroundColor: tag.color }}
            />
            <span className="flex-1 text-sm text-foreground">{tag.name}</span>
            <button
              onClick={() => setEditingTag(tag.tag_id)}
              className="p-2 hover:bg-accent/20 rounded transition-colors"
              title={t('edit')}
            >
              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => handleDeleteTag(tag.tag_id)}
              className="p-2 hover:bg-destructive/10 rounded transition-colors"
              title={t('delete')}
            >
              <svg className="w-4 h-4 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative bg-card rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            Tags
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent/10 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Create New Tag */}
          <div className="mb-6 p-4 bg-secondary/30 rounded-lg">
            <h3 className="text-sm font-medium text-foreground mb-3">{t('new')} Tag</h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="color"
                  value={newTagColor}
                  onChange={(e) => setNewTagColor(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-10 h-10"
                />
                <div
                  className="w-10 h-10 rounded-full border-2 border-border cursor-pointer flex items-center justify-center"
                  style={{ backgroundColor: newTagColor }}
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
              </div>
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder={t('tag_name') || 'Tag name...'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateTag();
                }}
                className="flex-1 px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background"
                data-testid="new-tag-name"
              />
              <button
                onClick={handleCreateTag}
                disabled={creating || !newTagName.trim()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50"
                data-testid="create-tag-button"
              >
                {creating ? '...' : '+'}
              </button>
            </div>

            {/* Color Presets */}
            <div className="flex gap-2 mt-3 flex-wrap">
              {TAG_COLORS.map(color => (
                <button
                  key={color}
                  onClick={() => setNewTagColor(color)}
                  className={`w-6 h-6 rounded-full transition-transform hover:scale-110 ${newTagColor === color ? 'ring-2 ring-offset-2 ring-primary' : ''}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Tags List */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">
              {t('all')} Tags ({tags.length})
            </h3>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              </div>
            ) : tags.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('no_tags') || 'No tags created yet.'}
              </p>
            ) : (
              <div className="space-y-1">
                {tags.map(tag => (
                  <TagItem key={tag.tag_id} tag={tag} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagManager;
